import { InboxOutlined } from '@ant-design/icons'
import { StackOverflowLogo } from '@phosphor-icons/react'
import { Upload } from 'antd'
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload'
import * as React from 'react'
import { useState } from 'react'

import { sanitizeFile } from '@/utils/attackPrevention'

const { Dragger } = Upload

export type Props = {
  id?: string
  name?: string
  multiple?: boolean
  files?: File[]
  accept?: string
  showUploadList?: boolean
  disabled?: boolean
  onChange?: (files: File[]) => void
  onError?: (error: Error) => void
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void
  allowDownload?: boolean
  handleDownload?: (file: UploadFile) => void
}

const formatFiles = (files: Props['files']) => {
  return files
    ?.filter((file) => !!file)
    .map((file) => ({
      uid: file.name,
      name: file.name,
      status: 'done' as const,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      lastModifiedDate: new Date(file.lastModified),
      originFileObj: file as RcFile,
    }))
}

const DraggableFileUpload = ({
  id,
  multiple = false,
  showUploadList = true,
  onChange,
  onError,
  files,
  allowDownload = false,
  handleDownload,
  ...props
}: Props) => {
  const [fileList, setFileList] = useState(formatFiles(files))

  const handleChange = React.useCallback(
    async ({ fileList }: UploadChangeParam<UploadFile>) => {
      if (!onChange) return

      const sanitizedFiles = await Promise.all(
        fileList?.map(async (file: UploadFile) => {
          if (file instanceof File) {
            return await sanitizeFile(file)
          }
          return await sanitizeFile(file?.originFileObj)
        })
      )

      const receiveSanitizedFiles = sanitizedFiles.filter(
        (file): file is File => !!file
      )
      setFileList(formatFiles(receiveSanitizedFiles))
      onChange(receiveSanitizedFiles)
    },
    [onChange]
  )

  return (
    <Dragger
      beforeUpload={() => false}
      fileList={fileList || []}
      maxCount={multiple ? undefined : 1}
      multiple={multiple}
      onChange={handleChange}
      {...props}
    >
      <p className="ant-upload-drag-icon flex w-full items-center justify-center">
        {!multiple && fileList?.length ? (
          <StackOverflowLogo className="text-5xl text-[#1c64f2]" />
        ) : (
          <InboxOutlined />
        )}
      </p>
      <p className="text-base text-gray-800">
        {!multiple && fileList?.length
          ? 'Click or drag to this area to send a new file to upload'
          : 'Click or drag file to this area to upload'}
      </p>
    </Dragger>
  )
}

export default DraggableFileUpload
